import React, { Component } from 'react';
import { observer, inject } from 'mobx-react';
import { observable, action } from 'mobx';
import styled from 'styled-components';
import { Helmet } from 'react-helmet';

import Routes from './Routes';

import { Fabric } from 'office-ui-fabric-react/lib/Fabric';
import { initializeIcons } from '@uifabric/icons';
import { Link } from 'office-ui-fabric-react/lib/Link';
import { Callout } from 'office-ui-fabric-react/lib/Callout';

import { localizable } from 'components/Localization';
import Prompt from 'components/Prompt';
import withAuth from 'components/AuthWrapper';
import withIntl from 'components/IntlWrapper';

import './styles.css';
import Watermark from '../../components/Watermark';
import dynamicImport from 'components/dynamicImport';
import { getContrastText } from '../../helpers/colors';
import { Icon } from 'office-ui-fabric-react';

import Cookies from 'js-cookie';

const Logo = dynamicImport({
  component: '/components/Logo',
});

initializeIcons();

//loadTheme({ ...theme });

const Wrapper = styled(Fabric)`
  display: grid;
  align-content: stretch;
  grid-template-columns: 10em auto 25em;
  grid-template-rows: 3em 3em auto;
  grid-template-areas:
    'logo title identity'
    'menu menu menu'
    'content content content';

  > .content-without-menu {
    grid-column: 1 / span 3;
    grid-row: 2 / span 2;
    position: relative;
  }

  > .title {
    grid-area: title;
    background: ${({ theme }) =>
      theme.palette.themeSecondary || theme.palette.themePrimary};
    color: ${({ theme }) =>
      getContrastText(
        theme.palette.themeSecondary || theme.palette.themePrimary
      )};
    padding: 0.2em 0.5em;

    display: grid;
    grid-template-columns: min-content minmax(0, min-content) minmax(
        auto,
        max-content
      );
    grid-column-gap: 0.5em;
    grid-template-areas: 'back collapsible label';

    .back {
      grid-area: back;
      padding-top: 0.15em;
      span {
        display: none;
      }
    }

    &,
    * {
      font-size: 1.5rem;
      line-height: 1.5;
      font-weight: 500;
    }

    > * {
      display: block;
      white-space: nowrap;
    }

    .collapsible {
      grid-area: collapsible;
      flex-shrink: 1;
      overflow: hidden;
      text-overflow: ellipsis;
      text-align: left;
    }

    .label {
      grid-area: label;
    }

    a {
      cursor: pointer;
    }
  }

  @media only screen and (min-height: 400px) {
    height: 100vh;

    > .content {
      overflow: auto;
    }
  }

  @media only screen and (max-width: 1023px) {
    grid-template-columns: 6em 1fr;
    grid-template-rows: 2em min-content 3em auto;
    grid-template-areas:
      'logo identity'
      'title title'
      'menu menu'
      'content content';

    .content-without-menu {
      grid-row: 3 / span 2;
    }
  }

  @media only screen and (max-width: 479px) {
    .title {
      grid-template-columns: min-content auto;
      grid-template-rows: repeat(auto, 2);
      grid-column-gap: 0.2em;
      grid-template-areas:
        'back collapsible'
        'label label';
    }
    .identity {
      font-size: 14px;
    }
  }

  > .identity {
    grid-area: identity;
    background: ${({ theme }) =>
      theme.palette.themeSecondary || theme.palette.themePrimary};
    padding-right: 0.5em;

    display: flex;
    flex-flow: row nowrap;
    justify-content: flex-end;
    align-items: center;
    color: ${({ theme }) =>
      getContrastText(
        theme.palette.themeSecondary || theme.palette.themePrimary
      )};

    > span {
      margin-right: 1em;
    }

    > * {
      color: ${({ theme }) =>
        getContrastText(
          theme.palette.themeSecondary || theme.palette.themePrimary
        )};
    }
  }

  > .logo {
    grid-area: logo;
    background: ${({ theme }) =>
      theme.palette.themeSecondary || theme.palette.themePrimary};
    display: flex;
    align-items: center;
    padding-left: 0.5em;
    img {
      max-height: 75%;
      max-width: 100%;
    }
  }

  > .menu {
    grid-area: menu;
  }

  > .content {
    grid-area: content;
    position: relative;
  }
`;

const SupportCallout = styled(Callout)`
  padding: 1em;
  @media screen and (max-width: 479px) {
    left: 0 !important;
    width: 100%;
  }
`;

@inject('app', 'user')
@localizable
@observer
class Dashboard extends Component {
  @observable showSignoutPrompt = false;

  componentWillUnmount() {
    this.showSignoutPrompt = false;
  }

  handleSignoutPrompt = () => {
    this.showSignoutPrompt = !this.showSignoutPrompt;
  };

  handleSwitchDashboard = () => {
    Cookies.set('dashboard2', 'true', { expires: 365 });
    window.location.href = '/dashboard';
    window.location.reload();
  };

  handleSignout = async () => {
    this.showSignoutPrompt = false;
    const { user, app } = this.props;

    const result = await user.signout();
    if (result && result.redirect) {
      window.location.href = `${app.config.oidc.authority}/signout`;
    }
  };

  @observable calloutVisible = false;

  handleCalloutToggle = action(() => {
    this.calloutVisible = !this.calloutVisible;
  });

  render() {
    const { app, user, t } = this.props;

    const watermark =
      app.config.watermark && app.config.watermark !== ''
        ? app.config.watermark
        : null;

    const name = user.profile.family_name
      ? `${user.profile.given_name} ${user.profile.family_name}`
      : user.profile.name;

    return (
      <Wrapper className="dashboard" theme={app.theme}>
        {app.data ? (
          <Helmet>
            {!!user.claims.locale && <html lang={user.claims.locale} />}
            <title>
              {t('page_title', { friendly_name: app.data.friendly_name })}
            </title>
            {!!app.theme.favicon_url && (
              <link rel="icon" href={app.theme.favicon_url} sizes="16x16" />
            )}
          </Helmet>
        ) : null}
        {watermark && <Watermark>{watermark}</Watermark>}
        <a href="/dashboard" className="logo">
          <Logo url={app.theme.logo_url} />
        </a>
        <div className="identity">
          <span>
            <i className="ms-Icon ms-Icon--Contact" aria-hidden="true" /> {name}
          </span>
          <span>
            <i
              className="ms-Icon ms-Icon--Help"
              style={{ cursor: 'pointer' }}
              ref={(element) => (this.supportIcon = element)}
              onClick={this.handleCalloutToggle}
              aria-hidden="true"
              title="Hilfe"
            />
          </span>
          <Link onClick={this.handleSignoutPrompt}>
            <i className="ms-Icon ms-Icon--SignOut" aria-hidden="true" />{' '}
            {t('Log out')}
          </Link>

          <Prompt
            title={t('Log out')}
            message={t('Do you really want to log out?')}
            opened={this.showSignoutPrompt}
            onSubmit={this.handleSignout}
            onClose={this.handleSignoutPrompt}
          />

          <SupportCallout
            role={'alertdialog'}
            gapSpace={0}
            target={this.supportIcon}
            onDismiss={this.handleCalloutToggle}
            setInitialFocus={true}
            hidden={!this.calloutVisible}
          >
            <div>
              <div
                className="ms-font-l ms-fontWeight-semibold"
                style={{ color: app.theme.palette.themePrimary }}
              >
                {t('Customer service contact')}:
              </div>
              <p>{t('Customer service p')}</p>
              <a
                href="https://support.propnow.com/de/help"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  float: 'left',
                  width: '5rem',
                  height: '2.7rem',
                  border: '1px solid #777',
                  borderRadius: '3px',
                  textAlign: 'center',
                  margin: '0 1rem 0 0.2rem',
                  justifyContent: 'center',
                  alignItems: 'center',
                  textDecoration: 'none',
                  display: 'flex',
                  color: '#333',
                }}
              >
                <span>F.A.Q.</span>
              </a>
              <div>
                {t('Tel')}:{' '}
                <a href={'tel:' + t('customer_service_telephone_href')}>
                  {t('customer_service_telephone')}
                </a>
              </div>
              <div>
                {t('E-Mail')}:{' '}
                <a href={'mailto:' + t('customer_service_email')}>
                  {t('customer_service_email')}
                </a>
              </div>

              <div style={{ textAlign: 'right' }}>
                <Icon
                  title="Zu neuen Dashboard wechseln (beta)"
                  iconName="AirTickets"
                  onClick={this.handleSwitchDashboard}
                />
              </div>
            </div>
          </SupportCallout>
        </div>

        <Routes />
      </Wrapper>
    );
  }
}

export default withAuth(withIntl(Dashboard));



// WEBPACK FOOTER //
// ./src/app/containers/Dashboard/index.jsx